// apps/roleforge-app/src/lib/converse-stream.ts
// Plan 3b-ii — Phase 1.1: Async-generator SSE consumer for the /converse endpoint.

import type { ConverseEvent } from './converse-types';

/**
 * POST a user message to the converse SSE endpoint and yield each
 * ConverseEvent as it arrives.
 *
 * On a non-OK response the generator yields one synthetic `error` event then
 * returns.  On a successful SSE stream it yields events until the readable
 * stream closes or until a `stream_end` / `error` event is received.
 */
export interface StreamConverseOptions {
  signal?: AbortSignal;
  actionKey?: string;
  resumeToken?: string;
  /**
   * Optional thread to write into. When omitted the server defaults to the
   * audit's owner thread. Stakeholder shells pass their session thread_id.
   */
  thread_id?: string;
}

export async function* streamConverse(
  apiBase: string,
  jwt: string,
  auditId: string,
  message: string,
  optionsOrSignal?: AbortSignal | StreamConverseOptions,
): AsyncGenerator<ConverseEvent, void, void> {
  // Backwards compatible: 5th arg used to be `signal?: AbortSignal`. If it
  // looks like an AbortSignal we treat it as one; otherwise we treat it as
  // the new StreamConverseOptions bag.
  const opts: StreamConverseOptions =
    optionsOrSignal && typeof (optionsOrSignal as AbortSignal).aborted === 'boolean'
      ? { signal: optionsOrSignal as AbortSignal }
      : (optionsOrSignal as StreamConverseOptions | undefined) ?? {};

  const url = `${apiBase}/api/roleforge/audits/${encodeURIComponent(auditId)}/converse`;

  // `cookie` is the sentinel value that means "use the httpOnly stakeholder
  // session cookie instead of a Bearer header". We send credentials so the
  // browser includes the cookie; the server's authMiddleware reads it.
  const useCookie = jwt === 'cookie';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (!useCookie) {
    headers.Authorization = `Bearer ${jwt}`;
  }
  if (opts.actionKey) headers['Idempotency-Key'] = opts.actionKey;

  const body: Record<string, unknown> = { message };
  if (opts.thread_id) body.thread_id = opts.thread_id;
  if (opts.resumeToken) body.resumeToken = opts.resumeToken;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      signal: opts.signal,
      credentials: useCookie ? 'include' : 'same-origin',
      headers,
      body: JSON.stringify(body),
    });
  } catch (err) {
    // Network-level failure (offline, CORS, AbortError, …)
    const message =
      err instanceof Error ? err.message : 'network error';
    yield { type: 'error', code: 'network_error', message };
    return;
  }

  if (!res.ok || !res.body) {
    // Parse JSON error body if available, fall back to status text.
    let code = 'http_error';
    let errorMessage = res.statusText;
    let cap_kind: string | undefined;
    try {
      const body = await res.json();
      if (body.code) code = body.code;
      if (body.message) errorMessage = body.message;
      else if (body.error) errorMessage = body.error;
      if (body.cap_kind) cap_kind = body.cap_kind;
    } catch {
      // ignore parse failure
    }
    yield { type: 'error', code, cap_kind, message: errorMessage };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on SSE double-newline boundaries.
      const parts = buffer.split('\n\n');
      // The last element may be an incomplete chunk — keep it in buffer.
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed || trimmed.startsWith(':')) {
          // comment or keepalive — skip
          continue;
        }
        // Find the `data:` line (there may be multiple lines per event block)
        for (const line of trimmed.split('\n')) {
          if (!line.startsWith('data:')) continue;
          const json = line.slice('data:'.length).trim();
          if (!json) continue;
          let ev: ConverseEvent;
          try {
            ev = JSON.parse(json) as ConverseEvent;
          } catch {
            // malformed frame — skip
            continue;
          }
          yield ev;
          if (ev.type === 'stream_end' || ev.type === 'error') {
            return;
          }
        }
      }
    }
  } catch (err) {
    // Issue #1200 — the body-reading loop had try/finally but no catch, so a
    // mid-stream network drop (wifi blip, laptop sleep, proxy timeout) threw
    // straight out of the generator: the caller's `for await` rejected, no
    // error event was ever dispatched, and the customer was left staring at a
    // half-written answer with no explanation and nothing to click.
    //
    // A dropped stream is now a normal terminal event, so every surface that
    // already handles `error` shows its retry affordance.
    if (isAbortError(err)) {
      // The caller aborted deliberately (navigation, unmount). Not an error
      // worth surfacing — end the stream quietly.
      return;
    }
    yield {
      type: 'error',
      code: 'stream_interrupted',
      message: err instanceof Error ? err.message : 'connection interrupted',
    };
    return;
  } finally {
    // releaseLock() throws if the reader is still mid-read; the stream is
    // being discarded either way, so never let cleanup mask the real failure.
    try {
      reader.releaseLock();
    } catch {
      /* nothing left to clean up */
    }
  }
}

function isAbortError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}
